import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { GETALLUSERS_API, createApproval } from "../../Constants/apiRoutes";
import { toast, ToastContainer } from "react-toastify";
import LoadingAnimation from "../../Components/Loading/LoadingAnimation";

const AddApprovalPopup = ({ isOpen, onClose, documentId }) => {
  const [activeTab, setActiveTab] = useState("search");
  const [searchText, setSearchText] = useState("");
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedInternalUsers, setSelectedInternalUsers] = useState([]);
  const [selectedExternalUsers, setSelectedExternalUsers] = useState([]);
  const [manualName, setManualName] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [externalUsers, setExternalUsers] = useState([]);
  const [isOpen1, setIsOpen1] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requiresSignature, setRequiresSignature] = useState(false);

  // Reset fields when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      resetFields();
    }
  }, [isOpen]);
  
  // Fetch users when searchText changes
  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [searchText]);

  const fetchUsers = async () => {
    const token = localStorage.getItem("token");
  
    try {
      const response = await axios.get(GETALLUSERS_API, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          searchText: searchText,
        },
      });
  
      setUsers(response.data.users);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };
  
  const handleAddUser = () => {
    if (manualName && manualEmail) {
      setExternalUsers([
        ...externalUsers,
        {
          id: Date.now(),
          name: manualName,
          email: manualEmail,
          RequiresSignature: requiresSignature,
        },
      ]);
      setManualName("");
      setManualEmail("");
      setRequiresSignature(false);
    }
  };

  const resetFields = () => {
    setSearchText("");
    setSelectedInternalUsers([]);
    setSelectedExternalUsers([]);
    setManualName("");
    setManualEmail("");
    setActiveTab("search");
    setRequiresSignature(false);
  };

  // Filter users based on searchText
  useEffect(() => {
    if (searchText.trim() === "") {
      setFilteredUsers([]);
      return;
    }
    const query = searchText.toLowerCase();
    const filtered = users.filter(
      (user) =>
        (user.FirstName?.toLowerCase() || "").includes(query) ||
        (user.LastName?.toLowerCase() || "").includes(query) ||
        (user.Email?.toLowerCase() || "").includes(query)
    );

    setFilteredUsers(filtered);
  }, [searchText, users]);

  const handleSelectUser = (user) => {
    if (!selectedInternalUsers.some((u) => u.UserID === user.UserID)) {
      setSelectedInternalUsers([
        ...selectedInternalUsers,
        { ...user, RequiresSignature: requiresSignature },
      ]);
    }
    setSearchText(""); // Clear search text after selecting a user
    setFilteredUsers([]); // Hide dropdown after selection
    setRequiresSignature(false);
  };

  const handleRemoveUser = (id) => {
    setExternalUsers((prevUsers) => prevUsers.filter((user) => user.id !== id));
  };

  const handleRemoveInternalUser = (userId) => {
    setSelectedInternalUsers((prevUsers) =>
      prevUsers.filter((user) => user.UserID !== userId)
    );
  };

  const handleToggleSignature = (userId) => {
    setSelectedInternalUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.UserID === userId
          ? { ...user, RequiresSignature: !user.RequiresSignature }
          : user
      )
    );
  };

  const handleToggleExternalSignature = (id) => {
    setExternalUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.id === id
          ? { ...user, RequiresSignature: !user.RequiresSignature }
          : user
      )
    );
  };

  const handleSendMail = async () => {
    if (selectedInternalUsers.length === 0 && externalUsers.length === 0) {
      toast.warning("Please select at least one user!");
      return;
    }

    console.log("Starting mail send...");
    setLoading(true);
    setIsOpen1(false); // Close the confirmation modal

    const token = localStorage.getItem("token");
    const tenantId = localStorage.getItem("TenantID");

    const payload = {
      documentId: documentId,
      approvalLevel: 1,
      status: "pending",
      comments: "Approval Request",
      tenantId: Number(tenantId),
      users: [
        ...selectedInternalUsers.map((user) => ({
          UserID: user.UserID,
          RequiresSignature: user.RequiresSignature || false,
        })),
        ...externalUsers.map((user) => ({
          FirstName: user.name,
          Email: user.email,
          IsInternal: false,
          RequiresSignature: user.RequiresSignature || false,
        })),
      ],
    };

    try {
   
      const response = await axios.post(createApproval, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      toast.success("Mail sent successfully!");

      onClose();
    } catch (error) {
      console.error("Error sending mail:", error);

       const errorMessage =
    error?.response?.data?.details ||
    error?.message ||
    "Failed to send mail."; 

  toast.error(errorMessage);
    } finally {
      console.log("Setting loading to false");
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      {loading && <LoadingAnimation />}
      <ToastContainer />
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-2xl flex flex-col">
        <h2 className="text-lg font-semibold mb-4">Add Approval</h2>

        <div className="flex border-b mb-4">
          <button
            className={`flex-1 py-2 text-center ${
              activeTab === "search"
                ? "border-b-2 border-[#8B4513] text-[#8B4513] font-semibold"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("search")}
          >
            Internal User
          </button>
          <button
            className={`flex-1 py-2 text-center ${
              activeTab === "manual"
                ? "border-b-2 border-[#8B4513] text-[#8B4513] font-semibold"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("manual")}
          >
            External User
          </button>
        </div>

        <div className="flex-grow">
          {activeTab === "search" ? (
            <>
            <div className="flex flex-col h-[200px] overflow-hidden">
              {/* Selected Users Display */}
              {selectedInternalUsers.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {selectedInternalUsers.map((user) => (
                    <span
                      key={user.UserID}
                      className="flex flex-col px-2 py-1 bg-gray-200 rounded-md text-sm mb-2"
                    >
                      <div className="flex items-center justify-between">
                        <span>
                          {user.FirstName} {user.LastName}
                        </span>
                        <button
                          onClick={() => handleRemoveInternalUser(user.UserID)}
                          className="text-red-500 ml-2"
                        >
                          ✕
                        </button>
                      </div>
                      <label className="inline-flex items-center mt-1">
                        <input
                          type="checkbox"
                          checked={user.RequiresSignature || false}
                          onChange={() => handleToggleSignature(user.UserID)}
                          className="form-checkbox h-4 w-4 text-[#8B4513]"
                        />
                        <span className="ml-1 text-xs">Signature</span>
                      </label>
                    </span>
                  ))}
                </div>
              )}

              {/* Search Bar and Signature Checkbox */}
              <div className="flex items-center mb-4">
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  className="flex-grow p-2 border rounded"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>

              {/* Search Results */}
              {searchText.trim() !== "" && filteredUsers.length > 0 && (
                <div className="max-h-40 border rounded mb-4 overflow-y-auto">
                  {filteredUsers.map((user) => (
                    <div
                      key={user.UserID}
                      className="p-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSelectUser(user)}
                    >
                      <p className="text-sm font-medium">
                        {user.FirstName} {user.LastName}
                      </p>
                      <p className="text-xs text-gray-500">{user.Email}</p>
                    </div>
                  ))}
                </div>
              )}
              </div>
            </>
          ) : (
            <div>
              {/* Input Fields */}
              <input
                type="text"
                placeholder="Enter name..."
                className="w-full p-2 border rounded mb-2"
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
              />
              <input
                type="email"
                placeholder="Enter email..."
                className="w-full p-2 border rounded mb-2"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
              />
              <div className="flex items-center mb-4">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={requiresSignature}
                    onChange={() => setRequiresSignature(!requiresSignature)}
                    className="form-checkbox h-5 w-5 text-[#8B4513]"
                  />
                  <span className="ml-2">Requires Signature</span>
                </label>
              </div>
              <button
                onClick={handleAddUser}
                className="w-full bg-[#8B4513] text-white py-2 rounded"
              >
                Add External User
              </button>

              {/* Display Added Users */}
              <div className="mt-4 flex flex-wrap gap-2">
                {externalUsers.map((user) => (
                  <span
                    key={user.id}
                    className="flex items-center px-2 py-1 bg-gray-200 rounded-md text-sm"
                  >
                    {user.name} ({user.email})
                    <div className="ml-2 flex items-center">
                      <label className="inline-flex items-center mr-2">
                        <input
                          type="checkbox"
                          checked={user.RequiresSignature || false}
                          onChange={() =>
                            handleToggleExternalSignature(user.id)
                          }
                          className="form-checkbox h-4 w-4 text-[#8B4513]"
                        />
                        <span className="ml-1 text-xs">Signature</span>
                      </label>
                      <button
                        onClick={() => handleRemoveUser(user.id)}
                        className="text-red-500"
                      >
                        ✕
                      </button>
                    </div>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-4 mt-4 border-t pt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#8B4513] text-white rounded"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={() => setIsOpen1(true)}
            className="px-4 py-2 bg-[#8B4513] text-white rounded"
            disabled={loading}
          >
            Send Mail
          </button>
        </div>
        {isOpen1 && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded shadow-lg">
              <p className="text-lg font-semibold mb-4">
                Are you sure you want to send the mail?
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setIsOpen1(false)}
                  className="px-4 py-2 bg-[#8B4513] text-white rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendMail}
                  className="px-4 py-2 bg-[#8B4513] text-white rounded"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddApprovalPopup;